import React, {KeyboardEvent, ReactNode, useEffect, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useSearchParams} from 'react-router-dom';
import {WyscoutAnalyticsDashboard as DashboardData, WyscoutAnalyticsFilters} from '@/features/players/wyscoutAnalyticsApi';
import {wyscoutAnalyticsQuery} from '@/features/players/queries';
import {SkeletonBlock} from '@/components/ui/LoadingSkeleton';
import LoadingStatus from '@/components/ui/LoadingStatus';
import AnalyticsToolbar from './AnalyticsToolbar';
import {
  defaultWyscoutAnalyticsLens,
  parseWyscoutAnalyticsFilters,
  parseWyscoutAnalyticsPeerIds,
  serializeWyscoutAnalyticsFilters,
  serializeWyscoutAnalyticsPeerIds,
} from './filters';
import KpiGrid from './KpiGrid';
import StatusPanels from './StatusPanels';
import ActionHeatmap from './ActionHeatmap';
import ShotMap from './ShotMap';
import {PassOriginMap, ProfileDistributions, RoleTrends} from './RolePresentation';
import AnalyticsSyncPanel from './AnalyticsSyncPanel';
import AIProfile from './AIProfile';
import AnalyticsDisclosure from './AnalyticsDisclosure';
import {AnalyticsSection, AnalyticsSectionPreferences, readAnalyticsSectionPreferences, writeAnalyticsSectionPreferences} from './sectionPreferences';

interface Props {
  workspaceId: string;
  playerId: string;
  playerName: string;
  position: string;
  matchLog: ReactNode;
}

type View = 'dashboard' | 'match-log' | 'ai-profile';

const analyticsKeys = ['analytics_lens', 'analytics_season', 'analytics_match', 'analytics_peer'];
const viewKey = 'analytics_view';
const lensLabels = {forward: 'Forward', midfielder: 'Midfielder', defender: 'Defender', goalkeeper: 'Goalkeeper'} as const;

const selectionLabel = (filters: WyscoutAnalyticsFilters, options: DashboardData['filterOptions']): string => {
  const season = filters.seasonId === null
    ? 'all seasons'
    : options.seasons.find(item => item.id === filters.seasonId)?.label ?? `season ${filters.seasonId}`;
  const matches = filters.matchIds.length === 0
    ? 'all matches'
    : filters.matchIds.map(id => options.matches.find(match => match.id === id)?.opponent ?? `match ${id}`).join(', ');
  return `${lensLabels[filters.lens]}, ${season}, ${matches}`;
};

const parseView = (params: URLSearchParams): View => {
  const value = params.getAll(viewKey).length === 1 ? params.get(viewKey) : null;
  return value === 'match-log' || value === 'ai-profile' ? value : 'dashboard';
};

const analyticsParams = (
  current: URLSearchParams,
  filters: WyscoutAnalyticsFilters,
  defaultLens: ReturnType<typeof defaultWyscoutAnalyticsLens>,
  view: View,
  peerIds: string[],
  playerId: string,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  [...analyticsKeys, viewKey].forEach(key => next.delete(key));
  if (view !== 'dashboard') next.set(viewKey, view);
  serializeWyscoutAnalyticsFilters(filters, defaultLens).forEach((value, key) => next.append(key, value));
  serializeWyscoutAnalyticsPeerIds(peerIds, playerId).forEach((value, key) => next.append(key, value));
  return next;
};

const WyscoutAnalyticsDashboard: React.FC<Props> = ({workspaceId, playerId, playerName, position, matchLog}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const defaultLens = defaultWyscoutAnalyticsLens(position);
  const filters = parseWyscoutAnalyticsFilters(searchParams, defaultLens);
  const peerIds = parseWyscoutAnalyticsPeerIds(searchParams, playerId);
  const view = parseView(searchParams);
  const query = useQuery(wyscoutAnalyticsQuery(workspaceId, playerId, filters));
  const retryingRef = useRef(false);
  const [retryingSelection, setRetryingSelection] = useState(false);
  const [sections, setSections] = useState<AnalyticsSectionPreferences>(readAnalyticsSectionPreferences);
  const matchLogKey = `${workspaceId}:${playerId}`;
  const [mountedMatchLogKey, setMountedMatchLogKey] = useState<string | null>(view === 'match-log' ? matchLogKey : null);
  const matchLogMounted = mountedMatchLogKey === matchLogKey;
  const errorCode = (query.error as {code?: string} | null)?.code;
  const retained = useRef<{key: string; data: DashboardData} | null>(null);
  const retentionKey = `${workspaceId}:${playerId}`;
  if (retained.current?.key !== retentionKey) retained.current = null;
  if (query.data) retained.current = {key: retentionKey, data: query.data};
  const renderedData: DashboardData | undefined = query.data ?? retained.current?.data;
  const pendingLens = query.isPlaceholderData && renderedData && filters.lens !== renderedData.filters.lens
    ? filters.lens
    : null;
  const visibleTabs: View[] = renderedData?.capabilities.includes('wyscout.analysis.view')
    ? ['dashboard', 'match-log', 'ai-profile']
    : ['dashboard', 'match-log'];
  const effectiveView: View = visibleTabs.includes(view) ? view : 'dashboard';

  const writeState = (nextFilters: WyscoutAnalyticsFilters, nextView: View, replace = false, nextPeerIds = peerIds) => {
    const current = paramsRef.current;
    const next = analyticsParams(current, nextFilters, defaultLens, nextView, nextPeerIds, playerId);
    if (next.toString() === current.toString()) return;
    paramsRef.current = next;
    setSearchParams(next, {replace});
  };

  const updateFilters = (nextFilters: WyscoutAnalyticsFilters) => writeState(nextFilters, view);

  const toggleSection = (section: AnalyticsSection) => {
    setSections(current => {
      const next = {...current, [section]: !current[section]};
      writeAnalyticsSectionPreferences(next);
      return next;
    });
  };

  const retrySelection = () => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetryingSelection(true);
    void query.refetch({cancelRefetch: false}).finally(() => {
      retryingRef.current = false;
      setRetryingSelection(false);
    });
  };

  const selectView = (nextView: View, focus = false) => {
    writeState(filters, nextView);
    if (focus) document.getElementById(`wyscout-analytics-tab-${nextView}`)?.focus();
  };

  useEffect(() => {
    if (view === 'match-log') setMountedMatchLogKey(matchLogKey);
  }, [matchLogKey, view]);

  useEffect(() => {
    const recoverValidationFilter = query.isError && errorCode === 'validation_failed'
      && (filters.seasonId !== null || filters.matchIds.length > 0);
    const authoritativeFilters = recoverValidationFilter
      ? {...filters, seasonId: null, matchIds: []}
      : query.data && !query.isPlaceholderData ? query.data.filters : filters;
    const current = paramsRef.current;
    const permittedView = view === 'ai-profile' && query.data && !query.data.capabilities.includes('wyscout.analysis.view') ? 'dashboard' : view;
    const next = analyticsParams(current, authoritativeFilters, defaultLens, permittedView, peerIds, playerId);
    if (next.toString() === current.toString()) return;
    paramsRef.current = next;
    setSearchParams(next, {replace: true});
  }, [defaultLens, errorCode, filters, peerIds, playerId, query.data, query.isError, query.isPlaceholderData, setSearchParams, view]);

  const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    let next: View | null = null;
    const currentIndex = visibleTabs.indexOf(effectiveView);
    if (event.key === 'ArrowRight') next = visibleTabs[(currentIndex + 1) % visibleTabs.length];
    if (event.key === 'ArrowLeft') next = visibleTabs[(currentIndex - 1 + visibleTabs.length) % visibleTabs.length];
    if (event.key === 'Home') next = visibleTabs[0];
    if (event.key === 'End') next = visibleTabs[visibleTabs.length - 1];
    if (next) {
      event.preventDefault();
      selectView(next, true);
    }
  };

  const dashboardPanel = query.isPending
    ? <div role="status" aria-label="Loading player analytics" aria-live="polite" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2].map(item => <SkeletonBlock key={item} className="h-28 overflow-hidden" />)}
    </div>
    : query.isError && !renderedData
      ? errorCode === 'not_found'
        ? <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">Player analytics are not available.</p>
        : <div className="rounded-lg bg-red-50 p-4 ring-1 ring-red-200 dark:bg-red-950/30 dark:ring-red-900">
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">Unable to load player analytics.</p>
          <button type="button" className="mt-3 min-h-11 rounded-md border border-red-300 px-3 font-semibold text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:border-red-800 dark:text-red-300" onClick={() => { void query.refetch(); }}>Retry analytics</button>
        </div>
      : renderedData && <div className="space-y-5">
        {pendingLens ? <LoadingStatus
          label={`Loading ${lensLabels[pendingLens]} analytics`}
          ariaLabel="Analytics selection status"
          description={`Showing ${lensLabels[renderedData.filters.lens]} analytics until ${lensLabels[pendingLens]} analytics are ready.`}
          className="w-full justify-start rounded-lg border-l-4 border-club-blue-600 bg-club-blue-50 p-4 text-club-blue-900 dark:bg-club-blue-950/40 dark:text-club-blue-100"
        /> : (query.isPlaceholderData || query.isError) && <div
          role={query.isError ? 'alert' : 'status'}
          aria-label="Analytics selection status"
          className={`rounded-lg border-l-4 p-4 text-sm font-semibold ${query.isError ? 'border-amber-600 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100' : 'border-club-blue-600 bg-club-blue-50 text-club-blue-900 dark:bg-club-blue-950/40 dark:text-club-blue-100'}`}
        >
          {query.isError ? 'Unable to update' : 'Updating'} {selectionLabel(filters, renderedData.filterOptions)}. Modules {query.isError ? 'still' : 'currently'} show {selectionLabel(renderedData.filters, renderedData.filterOptions)}.
          {query.isError && <button type="button" disabled={query.isFetching || retryingSelection} onClick={retrySelection} className="ml-3 min-h-11 rounded-md border border-amber-700 px-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-300">Retry requested analytics</button>}
        </div>}
        <AnalyticsToolbar filters={filters} options={renderedData.filterOptions} pendingLens={pendingLens} onChange={updateFilters} />
        <div data-testid="analytics-dashboard-content" aria-busy={pendingLens ? true : undefined} className="space-y-5">
          <StatusPanels data={renderedData} />
          <AnalyticsSyncPanel
            workspaceId={workspaceId}
            playerId={playerId}
            data={renderedData}
            isFetching={query.isFetching}
            refreshFailed={query.isError}
            dataUpdatedAt={query.dataUpdatedAt}
            onRefresh={() => { void query.refetch(); }}
          />
          <KpiGrid kpis={renderedData.kpis} />
          <div className="space-y-3" data-testid="analysis-disclosures">
          <AnalyticsDisclosure
            id="origins"
            label={renderedData.filters.lens === 'forward' || renderedData.filters.lens === 'goalkeeper'
              ? renderedData.shotMap.title || 'Origins'
              : renderedData.passMap.title || 'Origins'}
            summary="Pitch map and outcomes"
            open={sections.origins}
            onToggle={() => toggleSection('origins')}
          >
            {renderedData.filters.lens === 'forward' || renderedData.filters.lens === 'goalkeeper'
              ? <ShotMap map={renderedData.shotMap} lens={renderedData.filters.lens} embedded />
              : <PassOriginMap map={renderedData.passMap} embedded />}
          </AnalyticsDisclosure>
          <AnalyticsDisclosure id="heatmap" label={renderedData.heatmap.title || 'Action heatmap'} summary="Categories, body parts and zones" open={sections.heatmap} onToggle={() => toggleSection('heatmap')}>
            <ActionHeatmap heatmap={renderedData.heatmap} embedded />
          </AnalyticsDisclosure>
          <AnalyticsDisclosure id="profiles" label="Role profiles" summary={`${renderedData.profiles.length} profile ${renderedData.profiles.length === 1 ? 'card' : 'cards'}`} open={sections.profiles} onToggle={() => toggleSection('profiles')}>
            <ProfileDistributions profiles={renderedData.profiles} embedded />
          </AnalyticsDisclosure>
          <AnalyticsDisclosure id="trends" label="Match trends" summary="Per-match metric tables" open={sections.trends} onToggle={() => toggleSection('trends')}>
            <RoleTrends trends={renderedData.trends} playerId={renderedData.player.id} embedded />
          </AnalyticsDisclosure>
          </div>
        </div>
      </div>;

  return <div className="min-w-0 space-y-4">
    <nav aria-label="WyScout analytics sections" className="overflow-x-auto border-b border-gray-200 dark:border-gray-700">
      <div role="tablist" aria-label="WyScout analytics views" className="flex min-w-max gap-1">
        {visibleTabs.map(tab => {
          const label = tab === 'dashboard' ? 'Dashboard' : tab === 'match-log' ? 'Match log' : 'AI profile';
          return <button
            key={tab}
            id={`wyscout-analytics-tab-${tab}`}
            type="button"
            role="tab"
            aria-selected={effectiveView === tab}
            aria-controls={`wyscout-analytics-panel-${tab}`}
            tabIndex={effectiveView === tab ? 0 : -1}
            className={`min-h-11 whitespace-nowrap border-b-2 px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-club-blue-500 ${effectiveView === tab ? 'border-club-blue-600 text-club-blue-700 dark:text-club-blue-300' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'}`}
            onClick={() => selectView(tab)}
            onKeyDown={handleTabKey}
          >{label}</button>;
        })}
      </div>
    </nav>
    <div id="wyscout-analytics-panel-dashboard" role="tabpanel" aria-labelledby="wyscout-analytics-tab-dashboard" tabIndex={effectiveView === 'dashboard' ? 0 : -1} hidden={effectiveView !== 'dashboard'} className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500">
      {effectiveView === 'dashboard' && dashboardPanel}
    </div>
    <div id="wyscout-analytics-panel-match-log" role="tabpanel" aria-labelledby="wyscout-analytics-tab-match-log" tabIndex={effectiveView === 'match-log' ? 0 : -1} hidden={effectiveView !== 'match-log'} className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500">
      {matchLogMounted ? matchLog : null}
    </div>
    {visibleTabs.includes('ai-profile') && <div id="wyscout-analytics-panel-ai-profile" role="tabpanel" aria-labelledby="wyscout-analytics-tab-ai-profile" tabIndex={effectiveView === 'ai-profile' ? 0 : -1} hidden={effectiveView !== 'ai-profile'} className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500">
      {effectiveView === 'ai-profile' && renderedData && <AIProfile
        workspaceId={workspaceId}
        playerId={playerId}
        playerName={playerName}
        filters={renderedData.filters}
        filterOptions={renderedData.filterOptions}
        peerIds={peerIds}
        onPeerIdsChange={next => writeState(filters, view, false, next)}
        onChangeDashboard={() => selectView('dashboard', true)}
      />}
    </div>}
  </div>;
};

export default WyscoutAnalyticsDashboard;
